'use memo';

import { createFileRoute } from '@tanstack/react-router';
import { StatCardSkeleton } from '@/components/Card/StatCard';
import { Highlight, StatCardBase } from '@/components/Card/StatCard/variation/StatCardBase';
import { StatCardCount } from '@/components/Card/StatCard/variation/StatCardCount';
import { Grid, HStack, VStack } from '@/components/Layout';
import { Text } from '@/components/Typography';
import { useFormatDate } from '@/hooks/date';
import type { ContractActivity, ContractCategoryValue, ContractTypeValue, RecentContract, ScheduledContract } from '@/lib/api/orpc/contracts/contract-overview';
import { cn } from '@/lib/cn';
import { defaultNS, useTranslation } from '@/lib/i18n';
import { StatCardListSlot } from './-components/StatCardListSlot';
import { useContractOverviewActivity, useContractOverviewCount, useContractOverviewSchedule } from './-queries/useContractOverview';
export const Route = createFileRoute('/_staff/contract/_list/_placeholders/overview')({
  component: ContractOverview
});
export function ContractOverview() {
  const countQuery = useContractOverviewCount();
  const scheduleQuery = useContractOverviewSchedule();
  const activityQuery = useContractOverviewActivity();
  const {
    t
  } = useTranslation(defaultNS);
  const cases = t('pages.contract.overview.unit.cases');
  const types = t('pages.contract.overview.unit.types');
  const typeBreakdownData = CONTRACT_TYPE_ORDER.map(type => ({
    label: t(`common.contractType.${type}`),
    value: countQuery.data?.contractTypeCounts[type] ?? 0
  }));
  const categoryBreakdownData = CONTRACT_CATEGORY_ORDER.map(category => ({
    label: t(`common.contractCategory.${category}`),
    value: countQuery.data?.contractCategoryCounts[category] ?? 0
  }));
  return <VStack className="gap-6 px-12 py-5">
      <Grid columns={4} columnGap={24} rowGap={24}>
        {/* Row 1: KPI ×4 */}
        <TotalContractsSlot isLoading={countQuery.isLoading} total={countQuery.data?.totalCount ?? 0} recents={countQuery.data?.recentContracts ?? []} unitLabel={cases} />
        <BreakdownSlot isLoading={countQuery.isLoading} title={t('pages.contract.overview.executed.title')} total={countQuery.data?.executedCount ?? 0} data={(countQuery.data?.executedTopContractTypes ?? []).map(b => ({
        label: t(`common.contractType.${b.contractType}`),
        value: b.count
      }))} unitLabel={cases} />
        <BreakdownSlot isLoading={countQuery.isLoading} title={t('pages.contract.overview.inProgress.title')} total={countQuery.data?.inProgressCount ?? 0} data={(countQuery.data?.inProgressStatusBreakdown ?? []).map(b => ({
        label: t(`common.contractStatus.${b.contractStatus}`),
        value: b.count
      }))} unitLabel={cases} />
        <BreakdownSlot isLoading={countQuery.isLoading} title={t('pages.contract.overview.endedOrCancelled.title')} total={countQuery.data?.endedOrCancelledCount ?? 0} data={(countQuery.data?.endedOrCancelledStatusBreakdown ?? []).map(b => ({
        label: t(`common.contractStatus.${b.contractStatus}`),
        value: b.count
      }))} unitLabel={cases} />

        {/* Row 2: 内訳 ×2（col-span-2） */}
        <BreakdownSlot className="col-span-2" isLoading={countQuery.isLoading} title={t('pages.contract.overview.contractTypeBreakdown.title')} total={typeBreakdownData.filter(d => d.value > 0).length} data={typeBreakdownData} unitLabel={types} />
        <BreakdownSlot className="col-span-2" isLoading={countQuery.isLoading} title={t('pages.contract.overview.contractCategoryBreakdown.title')} total={categoryBreakdownData.filter(d => d.value > 0).length} data={categoryBreakdownData} unitLabel={types} />

        {/* Row 3: スケジュール / アクティビティ（col-span-2） */}
        <ScheduleSlotBox isLoading={scheduleQuery.isLoading} items={scheduleQuery.data?.items ?? []} t={t as unknown as TFn} />
        <ActivitySlotBox isLoading={activityQuery.isLoading} items={activityQuery.data?.items ?? []} t={t as unknown as TFn} />
      </Grid>
    </VStack>;
}

// 緩めの TFn 型: i18next の TFunction を ReturnType<typeof useTranslation>["t"] で
// 取り出すと tsgo の型推論が爆発する（namespace 解決 × オーバーロード × ParseKeys）。
// このスロット間で渡す t は単純なキー→文字列マッパーとして扱えば十分。
type TFn = (key: string) => string;
function SkeletonSlot({
  className
}: {
  className?: string;
}) {
  return <StatCardSkeleton className={cn('min-h-[240px]', className)} />;
}
function TotalContractsSlot(props: {
  isLoading: boolean;
  total: number;
  recents: RecentContract[];
  unitLabel: string;
}) {
  const {
    isLoading,
    total,
    recents,
    unitLabel
  } = props;
  const {
    t
  } = useTranslation(defaultNS, {
    keyPrefix: 'pages.contract.overview.total'
  });
  if (isLoading) return <SkeletonSlot />;
  return <StatCardBase title={t('title')} className="min-h-[240px] flex flex-col" highlight={<Highlight label={total.toLocaleString()} suffix={unitLabel} />}>
      <ul className="flex-1 flex flex-col gap-1.5 overflow-y-auto pr-1">
        {recents.map(c => <li key={c.id}>
            <Text variant="pSmall" className="line-clamp-1 text-neutral-on-fill">
              {c.contractTitle}
            </Text>
          </li>)}
      </ul>
    </StatCardBase>;
}
const CONTRACT_TYPE_ORDER = ['EMPLOYMENT', 'SERVICE', 'LEASE', 'PURCHASE', 'SAAS_SUBSCRIPTION', 'OFFICE_RENTAL', 'OTHER'] as const satisfies readonly ContractTypeValue[];
const CONTRACT_CATEGORY_ORDER = ['HUMAN_RESOURCE', 'DEVICE', 'SERVICE', 'FACILITY', 'OTHER'] as const satisfies readonly ContractCategoryValue[];
function BreakdownSlot(props: {
  isLoading: boolean;
  title: string;
  total: number;
  data: {
    label: string;
    value: number;
  }[];
  unitLabel: string;
  className?: string;
}) {
  const {
    isLoading,
    title,
    total,
    data,
    unitLabel,
    className
  } = props;
  if (isLoading) return <SkeletonSlot className={className} />;
  return <StatCardCount className={cn('min-h-[240px]', className)} title={title} highlight={{
    label: total.toLocaleString(),
    suffix: unitLabel
  }} data={data} />;
}
function ScheduleSlotBox(props: {
  isLoading: boolean;
  items: ScheduledContract[];
  t: TFn;
}) {
  const {
    isLoading,
    items,
    t
  } = props;
  const formatDate = useFormatDate();
  if (isLoading) return <SkeletonSlot className="col-span-2" />;
  return <StatCardListSlot className="col-span-2" title={t('pages.contract.overview.schedule.title')} items={items} emptyLabel={t('pages.contract.overview.schedule.emptyLabel')} getItemKey={item => item.id} renderItem={item => <HStack className="items-baseline justify-between gap-3">
          <Text variant="pSmall" className="line-clamp-1 text-neutral-on-fill">
            {item.contractTitle}
          </Text>
          <Text variant="pSmall" className="whitespace-nowrap text-neutral-muted-on-fill">
            {formatDate(item.contractEndDate, {
        preset: 'dateWeekday'
      })}
          </Text>
        </HStack>} />;
}
function ActivitySlotBox(props: {
  isLoading: boolean;
  items: ContractActivity[];
  t: TFn;
}) {
  const {
    isLoading,
    items,
    t
  } = props;
  const formatDate = useFormatDate();
  if (isLoading) return <SkeletonSlot className="col-span-2" />;
  const fallbackTitle = t('pages.contract.overview.activity.untitledContract');
  return <StatCardListSlot className="col-span-2" title={t('pages.contract.overview.activity.title')} items={items} emptyLabel={t('pages.contract.overview.activity.emptyLabel')} getItemKey={item => item.id} renderItem={item => <HStack className="items-baseline justify-between gap-3">
          <Text variant="pSmall" className="line-clamp-1 text-neutral-on-fill">
            {item.contractTitle ?? fallbackTitle}
          </Text>
          <Text variant="pSmall" className="whitespace-nowrap text-neutral-muted-on-fill">
            {formatDate(item.occurredAt, {
        preset: 'dateTimeFullWeekday'
      })}
          </Text>
        </HStack>} />;
}